import React, { useEffect, useState } from "react";
import axios from "axios";
import "./styles/ReportViewer.css";
import customerlogo from "./assets/ajay.jpeg";
import ReportGraph from "./ReportGraph"; // Import ReportGraph component

export default function DailyReportViewer() {
  const [masterSites, setMasterSites] = useState([]);
  const [selectedMasterSiteId, setSelectedMasterSiteId] = useState("");
  const [activeTab, setActiveTab] = useState("daily");
  const [selectedMasterSiteName, setSelectedMasterSiteName] = useState("");

  const [dailyDate, setDailyDate] = useState("");
  const [monthlyMonth, setMonthlyMonth] = useState("");
  const [yearlyYear, setYearlyYear] = useState("");
  const [reports, setReports] = useState([]);

  const token = localStorage.getItem("token");
  const authHeader = {
    headers: { Authorization: `Bearer ${token}` },
  };

  const extractDecimal = (value) => {
    if (!value) return 0;
    if (typeof value === "object" && value?.$numberDecimal)
      return parseFloat(value.$numberDecimal);
    return parseFloat(value) || 0;
  };

  const computeTotals = () => {
    let totalWater = 0;
    let totalAmount = 0;
    let cardRechargeAmount = 0;
    let cardSwipeAmount = 0;

    reports.forEach((r) => {
      totalWater += extractDecimal(r.totalWaterDispensed || 0);
      totalAmount += extractDecimal(r.totalAmountCollected || 0);
      cardRechargeAmount += extractDecimal(r.cardRechargeAmount || 0);
      cardSwipeAmount += extractDecimal(r.cardSwipeAmount || 0);
    });

    return {
      totalWater: totalWater.toFixed(1),
      totalAmount: totalAmount.toFixed(2),
      cardRechargeAmount: cardRechargeAmount.toFixed(2),
      cardSwipeAmount: cardSwipeAmount.toFixed(2),
    };
  };

  // figure out if selected master site is ACCORD_THREE_TAP
  const selectedMasterSite = masterSites.find((s) => s._id === selectedMasterSiteId);
  const isThreeTap = selectedMasterSite?.dispenserType === "ACCORD_THREE_TAP";

  const downloadExcel = () => {
    const XLSX = require("xlsx");

    const data = reports.map((r) => {
      const base = {
        Site: r.site,
        Date: r.date,
        "Water (L)": extractDecimal(r.totalWaterDispensed).toFixed(1),
        "Amount (₹)": extractDecimal(r.totalAmountCollected).toFixed(2),
        "Card Recharge (₹)": extractDecimal(r.cardRechargeAmount).toFixed(2),
        "Card Swipe (₹)": extractDecimal(r.cardSwipeAmount).toFixed(2),
        TDS: extractDecimal(r.tds),
        pH: extractDecimal(r.ph),
      };
      if (selectedMasterSiteName === "Konark") {
        base["Input Flow (LPH)"] = r.inputWaterFlow;
        base["Output Flow (LPH)"] = r.outputWaterFlow;
        base["% Recovery"] = extractDecimal(r.percentRecovery).toFixed(2);
        base["Status"] = r.dailyWorkingStatus;
        base["Users"] = r.users;
      }
      // Three-tap extras
      if (isThreeTap) {
        base["Tap1 (L)"] = extractDecimal(r.tap1WaterDispensed).toFixed(1);
        base["Tap2 (L)"] = extractDecimal(r.tap2WaterDispensed).toFixed(1);
        base["Tap3 (L)"] = extractDecimal(r.tap3WaterDispensed).toFixed(1);
        base["Temp1 (°C)"] = extractDecimal(r.temp1);
        base["Temp2 (°C)"] = extractDecimal(r.temp2);
        base["Tank1 (%)"] = extractDecimal(r.tank1);
        base["Tank2 (%)"] = extractDecimal(r.tank2);
      }

      return base;
    });

    const totals = computeTotals();
    const totalRow = {
      Site: "Total",
      Date: "",
      "Water (L)": totals.totalWater,
      "Amount (₹)": totals.totalAmount,
      "Card Recharge (₹)": totals.cardRechargeAmount,
      "Card Swipe (₹)": totals.cardSwipeAmount,
    };
    data.push(totalRow);

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

    const blob = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "binary",
    });

    function s2ab(s) {
      const buf = new ArrayBuffer(s.length);
      const view = new Uint8Array(buf);
      for (let i = 0; i < s.length; i++) {
        view[i] = s.charCodeAt(i) & 0xff;
      }
      return buf;
    }

    const filename = `${activeTab}_report_${new Date().toISOString().slice(0, 10)}.xlsx`;
    const FileSaver = require("file-saver");
    FileSaver.saveAs(new Blob([s2ab(blob)], { type: "application/octet-stream" }), filename);
  };

  const fetchMasterSites = async () => {
    try {
      const res = await axios.get("/api/mastersites", authHeader);
      setMasterSites(res.data);
      if (res.data.length > 0) {
        setSelectedMasterSiteId(res.data[0]._id);
        setSelectedMasterSiteName(res.data[0].name);
      }
    } catch (err) {
      console.error("Error fetching master sites:", err);
    }
  };

  const fetchReport = async () => {
    if (!selectedMasterSiteId) return;
    let url = `/api/reports/${activeTab}/master/${selectedMasterSiteId}`;
    if (activeTab === "daily" && dailyDate) {
      url += `?date=${dailyDate}`;
    } else if (activeTab === "monthly" && monthlyMonth) {
      url += `?month=${monthlyMonth}`;
    } else if (activeTab === "yearly" && yearlyYear) {
      url += `?year=${yearlyYear}`;
    } else {
      alert("Please select a date/month/year");
      return;
    }

    try {
      const res = await axios.get(url, authHeader);
      setReports(res.data);
    } catch (err) {
      console.error("Failed to fetch report:", err);
    }
  };

  useEffect(() => {
    fetchMasterSites();
  }, []);

  const renderFilters = () => {
    if (activeTab === "daily") {
      return (
        <input
          type="date"
          className="input-control"
          onChange={(e) => {
            setReports([]);
            const d = new Date(e.target.value);
            const day = String(d.getDate()).padStart(2, "0");
            const month = String(d.getMonth() + 1).padStart(2, "0");
            const year = d.getFullYear();
            setDailyDate(`${day}/${month}/${year}`);
          }}
        />
      );
    } else if (activeTab === "monthly") {
      return (
        <input
          type="month"
          className="input-control"
          onChange={(e) => {
            setReports([]);
            const [year, month] = e.target.value.split("-");
            setMonthlyMonth(`/${month}/${year}`);
          }}
        />
      );
    } else if (activeTab === "yearly") {
      return (
        <select className="input-control" onChange={(e) => setYearlyYear(e.target.value)}>
          <option value="">Select year</option>
          <option value="2020">2020</option>
          <option value="2021">2021</option>
          <option value="2022">2022</option>
          <option value="2023">2023</option>
          <option value="2024">2024</option>
          <option value="2025">2025</option>
        </select>
      );
    }
  };

  return (
    <div className="report-viewer-container">
      <div className="report-meta-beautiful">
        <div className="title-area">
          {isThreeTap && (
                  <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                padding: "8px 12px",
                                border: "1px solid #e0e0e0",
                                borderRadius: "8px",
                                transition: "all 0.3s ease",
                                cursor: "pointer",
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor = "#f0f8ff";
                                e.currentTarget.style.transform = "translateY(-2px)";
                                e.currentTarget.style.boxShadow = "0 4px 10px rgba(0,0,0,0.1)";
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor = "transparent";
                                e.currentTarget.style.transform = "none";
                                e.currentTarget.style.boxShadow = "none";
                              }}
                            >
                              <img
                                src={customerlogo}
                                alt="Customer Logo"
                                style={{
                                  width: "45px",
                                  height: "45px",
                                  borderRadius: "8px",
                                  objectFit: "contain",
                                }}
                              />
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: "15px",
                                  fontWeight: "500",
                                  color: "#222",
                                }}
                              >
                                Ajay Infra & Construction
                              </p>
                            </div>)}
          <h2 className="report-heading">Water Dispensing Reports</h2>
          <p className="report-type">
            {activeTab === "daily" && "Daily Report"}
            {activeTab === "monthly" && "Monthly Report"}
            {activeTab === "yearly" && "Yearly Report"}
          </p>
          <p className="selected-info">
            {activeTab === "daily" && dailyDate && `📅 ${dailyDate}`}
            {activeTab === "monthly" && monthlyMonth && `📆 ${monthlyMonth}`}
            {activeTab === "yearly" && yearlyYear && `📈 ${yearlyYear}`}
          </p>
          {reports.length > 0 && (() => {
            const { totalWater, totalAmount, cardRechargeAmount, cardSwipeAmount } = computeTotals();
            return (
              <div className="summary-cards">
                <div className="card water">
                  💧 <span className="label">Total Water</span>
                  <span className="value">{totalWater} L</span>
                </div>
                <div className="card amount">
                  💰 <span className="label">Total Amount</span>
                  <span className="value">₹{totalAmount}</span>
                </div>
                <div className="card card-recharge">
                  💳 <span className="label">Card Recharge</span>
                  <span className="value">₹{cardRechargeAmount}</span>
                </div>
                <div className="card card-swipe">
                  💳 <span className="label">Card Swipe</span>
                  <span className="value">₹{cardSwipeAmount}</span>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      <div className="tab-buttons">
        {["daily", "monthly", "yearly"].map((tab) => (
          <button
            key={tab}
            className={`tab-button ${activeTab === tab ? "active" : ""}`}
            onClick={() => {
              setActiveTab(tab);
              setReports([]);
              setDailyDate("");
              setMonthlyMonth("");
              setYearlyYear("");
            }}
          >
            {tab.toUpperCase()}
          </button>
        ))}
      </div>

      <div className="filters">
        <select
          className="input-control"
          value={selectedMasterSiteId}
          onChange={(e) => {
            const site = masterSites.find((s) => s._id === e.target.value);
            setSelectedMasterSiteId(e.target.value);
            setSelectedMasterSiteName(site?.name || "");
          }}
        >
          {masterSites.map((site) => (
            <option key={site._id} value={site._id}>
              {site.name}
            </option>
          ))}
        </select>

        {renderFilters()}

        <button className="btn fetch" onClick={fetchReport}>
          Fetch
        </button>
        {reports.length > 0 && (
          <button className="btn download" onClick={downloadExcel}>
            Download Excel
          </button>
        )}
      </div>

      {reports.length === 0 ? (
        <p className="no-data">No records to display.</p>
      ) : (
        <>
          <table className="report-table">
            <thead>
              <tr>
                <th>Site</th>
                <th>Date</th>
                <th>Water (L)</th>
                <th>Amount (₹)</th>
                <th>Card Recharge (₹)</th>
                <th>Card Swipe (₹)</th>
                <th>TDS</th>
                <th>pH</th>
                {selectedMasterSiteName === "Konark" && activeTab !== "yearly" && (
                  <>
                    <th>Input Flow (LPH)</th>
                    <th>Output Flow (LPH)</th>
                    <th>% Recovery</th>
                    <th>Users</th>
                  </>
                )}
                {selectedMasterSiteName === "Konark" && activeTab === "daily" && (
                  <th>Status</th>
                )}
                {selectedMasterSiteName === "Konark" && activeTab === "monthly" && (
                  <>
                    <th>Bacteria Testing</th>
                    <th>Bacteria Testing Date</th>
                  </>
                )}
                {/* NEW: If selected master site is ACCORD_THREE_TAP, render these */}
                {isThreeTap && (
                  <>
                    <th>Tap1 (L)</th>
                    <th>Tap2 (L)</th>
                    <th>Tap3 (L)</th>
                    <th>Temp1 (°C)</th>
                    <th>Temp2 (°C)</th>
                    <th>Tank1 (%)</th>
                    <th>Tank2 (%)</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {reports.map((r, idx) => (
                <tr key={idx}>
                  <td>{r.site}</td>
                  <td>{r.date}</td>
                  <td>{extractDecimal(r.totalWaterDispensed).toFixed(1)}</td>
                  <td>{extractDecimal(r.totalAmountCollected).toFixed(2)}</td>
                  <td>{extractDecimal(r.cardRechargeAmount).toFixed(2)}</td>
                  <td>{extractDecimal(r.cardSwipeAmount).toFixed(2)}</td>
                  <td>{extractDecimal(r.tds)}</td>
                  <td>{extractDecimal(r.ph)}</td>
                  {selectedMasterSiteName === "Konark" && activeTab !== "yearly" && (
                    <>
                      <td>{r.inputWaterFlow}</td>
                      <td>{r.outputWaterFlow}</td>
                      <td>{extractDecimal(r.percentRecovery).toFixed(2)}</td>
                      <td>{r.users}</td>
                    </>
                  )}
                  {selectedMasterSiteName === "Konark" && activeTab === "daily" && (
                    <td>{r.dailyWorkingStatus}</td>
                  )}
                  {selectedMasterSiteName === "Konark" && activeTab === "monthly" && (
                    <>
                      <td>{r.bacteriaTesting}</td>
                      <td>{r.bacteriaTestingDate}</td>
                    </>
                  )}
                  {/* NEW: Three-tap extras */}
                  {isThreeTap && (
                    <>
                      <td>{extractDecimal(r.tap1WaterDispensed).toFixed(1)}</td>
                      <td>{extractDecimal(r.tap2WaterDispensed).toFixed(1)}</td>
                      <td>{extractDecimal(r.tap3WaterDispensed).toFixed(1)}</td>
                      <td>{extractDecimal(r.temp1)}</td>
                      <td>{extractDecimal(r.temp2)}</td>
                      <td>{extractDecimal(r.tank1)}</td>
                      <td>{extractDecimal(r.tank2)}</td>
                    </>
                  )}
                </tr>
              ))}


            </tbody>
          </table>
          {/* ✅ Move the graph here inside the same block */}
          <ReportGraph reports={reports} activeTab={activeTab} />
        </>
      )}
    </div>
  );
}